<template>
	<div class="job-list-page">
		<div class="hero-banner" :style="heroBannerStyle">
			<h2>{{ heroTitle }}</h2>
		</div>

		<div class="search-area">
			<div class="search-row">
				<div class="search-input-wrap">
					<van-icon name="search" class="search-icon" />
					<input
						v-model.trim="keyword"
						type="text"
						placeholder="请输入职位关键词搜索"
						@keyup.enter="handleSearch"
					/>
				</div>
				<button class="search-btn" type="button" @click="handleSearch">职位搜索</button>
			</div>

			<div class="filter-row">
				<van-dropdown-menu class="type-filter" active-color="#333">
					<van-dropdown-item v-model="selectedType" :options="typeOptions" />
				</van-dropdown-menu>
				<button class="filter-trigger" type="button" @click="openCityPopup">
					<span class="filter-trigger-text">工作地点</span>
					<van-icon name="arrow-down" />
				</button>
			</div>
		</div>

		<div class="job-list">
			<template v-if="allJobs.length">
				<div
					class="job-item"
					v-for="item in allJobs"
					:key="item.id"
					@click="goDetail(item.id)"
				>
					<div class="title-row">
						<h3>{{ item.postName || "" }}</h3>
						<span class="salary">{{ item.salary || "" }}</span>
					</div>

					<div class="tag-row">
						<span class="tag">{{ item.recruitTypeStr || "" }}</span>
						<span class="tag">{{ item.postTypeStr || "" }}</span>
						<span v-if="item.recruitPlanStr" class="tag">{{ item.recruitPlanStr }}</span>
					</div>

					<div class="meta-row">
						<span class="publish">{{ getTime(item.createTime) }} 发布</span>
						<span class="location">
							<van-icon name="location-o" />
							<span class="tag">{{ formatLocationText(item) }}</span>
						</span>
					</div>
				</div>
			</template>

			<div v-else class="empty-wrap">
				<van-empty image="search" description="暂无职位数据" />
			</div>
		</div>

		<select-city-pop
			v-model:show="showCityPopup"
			:options="cityOptions"
			:selected-values="selectedCity"
			@confirm="handleCityConfirm"
		/>
	</div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Toast } from "vant";
import selectCityPop from "@/components/selectCityPop.vue";
import { getJobList, getLocationList } from "@/api/index.js";
import { CHANNELCODE } from "@/utils/constants";
import { getTime } from "@/utils/utils.js";

const route = useRoute();
const router = useRouter();

const keyword = ref("");
const selectedType = ref("all");
const selectedCity = ref([]);
const allJobs = ref([]);
const showCityPopup = ref(false);
const cityOptions = ref([]);
// const locationIdMap = ref(new Map());
// 原始城市列表
const rawCityList = ref([]);

const RECRUIT_TYPE_BY_SCENE = {
    social: "ZPLX_1",
    campusIntern: "ZPLX_2",
    campusGraduate: "ZPLX_3",
};

const STORAGE_KEY = "job_list_filters";

const RECRUIT_CONFIG = {
	social: {
		title: () => "",
		background: "http://pic5.40017.cn/i/ori/1ODBOYOjKy4.png",
	},
	// 实习生
	campusIntern: {
		// title: (year) => `${year}年酒店应届毕业生招聘`,
		title: () => "",
		background: "http://pic5.40017.cn/i/ori/1ODBQxQi5IQ.png",
	},
	// 应届生
	campusGraduate: {
		// title: (year) => `${year}年酒店在校实习生招聘`,
		title: () => "",
		background: "http://pic5.40017.cn/i/ori/1ODBSNxM4Lu.png",
	},
};

const typeOptions = [
	{ text: "全部", value: "all" },
	{ text: "酒店管理岗", value: "1" },
	{ text: "酒店开发岗", value: "2" },
	{ text: "职能支持岗", value: "3" },
	{ text: "门店类岗位", value: "4" },
];

const currentYear = computed(() => new Date().getFullYear());

const currentScene = computed(() => {
	const recruitType = route.query.recruitType && String(route.query.recruitType);
	if (recruitType && RECRUIT_TYPE_BY_SCENE[recruitType]) {
		return recruitType;
	}

	return route.query.entryType === "social" ? "social" : "campusGraduate";
});

const currentRecruitType = computed(() => {
	return RECRUIT_TYPE_BY_SCENE[currentScene.value] || RECRUIT_TYPE_BY_SCENE.campusGraduate;
});

const heroConfig = computed(() => {
	return RECRUIT_CONFIG[currentScene.value] || RECRUIT_CONFIG.campusGraduate;
});

const heroTitle = computed(() => {
	const title = heroConfig.value.title;
	return typeof title === "function" ? title(currentYear.value) : title;
});

const heroBannerStyle = computed(() => ({
	background: `url("${heroConfig.value.background}") no-repeat center`,
	backgroundSize: "cover",
}));

const getLocationText = (job) => {
  const source = job.recruitLocationVo;
  if (!source) {
    return "--";
  }

  const locationType = Number(source.type);
  if (locationType === 0) {
    return "全国（中国大陆）";
  }

  // 提取名称数组
  const pickNames = (list) => (Array.isArray(list) ? list : []).map((item) => item?.name).filter(Boolean);
  
  // 优先级：区 > 市 > 省 > 国家
  const prioritizedNames = [
    pickNames(source.district),
    pickNames(source.city),
    pickNames(source.province),
    pickNames(source.country),
  ].find((list) => list.length > 0) || [];

  return prioritizedNames.length ? prioritizedNames.join("、") : "--";
};

const formatLocationText = (job) => {
	// const locationText = getJobLocationText(job);
	const locationText = getLocationText(job);
	return locationText.length > 18 ? `${locationText.slice(0, 18)}...` : locationText;
};

// 保存筛选条件到缓存
const saveFiltersToStorage = () => {
  const data = {
    keyword: keyword.value,
    selectedType: selectedType.value,
    selectedCity: selectedCity.value,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// 从缓存加载筛选条件
const loadFiltersFromStorage = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      keyword.value = parsed.keyword || "";
      selectedType.value = parsed.selectedType || "all";
      selectedCity.value = parsed.selectedCity || [];
    }
  } catch (e) {
    console.log("加载缓存失败", e);
  }
};

const handleSearch = () => {
	saveFiltersToStorage();
	fetchJobs();
};

const openCityPopup = () => {
	showCityPopup.value = true;
};

const handleCityConfirm = (values) => {
	selectedCity.value = values;
	saveFiltersToStorage();
};

const goDetail = (id) => {
	if (!id) return;
	sessionStorage.setItem("from_job_list", "yes"); 
	router.push(`/joinUs/jobDetail/${id}`);
};

const fetchCityOptions = async () => {
  const params = {
    recruitTypeList: [currentRecruitType.value],
  };
  const cityList = (await getLocationList(params)) || [];

  // 保存原始数据
  rawCityList.value = cityList;

  const uniqueCityMap = new Map();
  cityList.forEach((item) => {
    if (!item || !item.id) return;
    const key = String(item.id);
    uniqueCityMap.set(key, {
      text: item.name || "",
      value: key,
    });
  });

  cityOptions.value = Array.from(uniqueCityMap.values());
};

const fetchJobs = async () => {
	Toast.loading({
		message: "加载中...",
		forbidClick: true,
		duration: 0,
	});

	const channel = route.query.channel || "official";
	const params = {
		equalRecruitChannel: CHANNELCODE[channel] ?? 0,
		recruitTypeList: [currentRecruitType.value],
	};

	if (keyword.value) {
		params.likePostName = keyword.value;
	}

	if (selectedType.value !== "all") {
		params.postTypeList = selectedType.value;
	}

	// if (selectedCity.value.length) {
	// 	const locationIdList = Array.from(
	// 		new Set(
	// 			selectedCity.value.map((id) => locationIdMap.value.get(String(id)) || String(id))
	// 		)
	// 	);
	// 	params.locationIdList = locationIdList;
	// }

	if (selectedCity.value.length) {
	// 收集所有 locationId
	let allLocationIds = [];
	selectedCity.value.forEach((cityValue) => {
		// 找到原始城市数据
		const item = rawCityList.value.find(
		(r) => String(r.id) === cityValue
		);
		if (item && Array.isArray(item.locationIdList)) {
		// 转字符串
		const strIds = item.locationIdList.map((id) => String(id));
		allLocationIds = allLocationIds.concat(strIds);
		}
	});

	// 去重
	const locationIdList = Array.from(new Set(allLocationIds));
	params.locationIdList = locationIdList;
	}

	try {
		const res = await getJobList(params);
		allJobs.value = (res && res.rows) || [];
	} catch (error) {
		Toast.fail("加载失败，请稍后重试");
	} finally {
		Toast.clear();
	}
};

onMounted(async () => {
	const fromDetail = sessionStorage.getItem("from_job_list") === "yes";
	sessionStorage.removeItem("from_job_list");

	await fetchCityOptions();

	if (fromDetail) {
		loadFiltersFromStorage(); // 恢复缓存
	} else {
		sessionStorage.removeItem(STORAGE_KEY); // 清空缓存
	}

	fetchJobs();
});

watch(selectedType, () => {
	saveFiltersToStorage();
	fetchJobs();
});

watch(
	selectedCity,
	() => {
		saveFiltersToStorage();
		fetchJobs();
	},
	{ deep: true }
);

watch(keyword, () => {
  saveFiltersToStorage();
});

watch(
	() => [route.query.recruitType, route.query.entryType, route.query.channel],
	() => {
		fetchJobs();
	}
);
</script>

<style lang="scss" scoped>
.job-list-page {
	background: #f3f3f3;
	min-height: calc(100vh - 91px);
}

.hero-banner {
	height: 140rem;
	padding: 96rem 40rem 0;
	box-sizing: border-box;

	h2 {
		color: rgba(0, 116, 148, 1);
		font-size: 30rem;
		font-weight: 600;
		margin: 0;
	}
}

.search-area {
	// background: #f3f3f3;
	background: #fff;
	padding: 14rem 20rem 10rem;

	.search-row {
		display: flex;
		// gap: 12rem;
	}

	.search-input-wrap {
		flex: 1;
		height: 58rem;
		background: #fff;
		// border-radius: 8rem;
        border-radius: 8rem 0 0 8rem !important;
		border: 1px solid #ececec;
        border-right: none;
		display: flex;
		align-items: center;
		padding: 0 16rem;
		box-sizing: border-box;

		.search-icon {
			color: #bcbcbc;
			font-size: 28rem;
			margin-right: 10rem;
		}

		input {
			flex: 1;
			border: none;
			outline: none;
			font-size: 24rem;
			color: #333;
			background: transparent;
		}
	}
}

.search-btn {
	width: 150rem;
	height: 56rem;
	border-radius: 6rem;
	background: #005a57;
	color: #fff;
	font-size: 26rem;
	border-color: #005a57;
}

.filter-row {
	margin-top: 14rem;
	display: flex;
	gap: 12rem;
}

.type-filter {
	flex: 1;

	:deep(.van-dropdown-menu__bar) {
		height: 56rem;
		box-shadow: none;
		background: #efefef;
		border-radius: 8rem;
	}

	:deep(.van-dropdown-menu__item) {
		flex: 1;
	}

	:deep(.van-dropdown-menu__title) {
		font-size: 26rem;
		color: #555;
	}
}

.filter-trigger {
	flex: 1;
	height: 56rem;
	border: none;
	border-radius: 8rem;
	background: #efefef;
	padding: 0 20rem;
	color: #555;
	font-size: 26rem;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;

	:deep(.van-icon) {
		position: absolute;
		right: 20rem;
	}
}

.filter-trigger-text {
	width: 100%;
	text-align: center;
}

.job-list {
	background: #fff;
	padding: 0 20rem;
}

.empty-wrap {
	padding: 40rem 0;
}

.job-item {
	border-bottom: 1px solid #efefef;
	padding: 24rem 8rem 20rem;
}

.title-row {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: 16rem;

	h3 {
		margin: 0;
		// font-size: 46rem;
        font-size: 31rem;
		line-height: 1.25;
		color: #333;
		font-weight: 500;
	}

	.salary {
		color: #ff7a00;
		// font-size: 42rem;
        font-size: 27rem;
		font-weight: 700;
		white-space: nowrap;
	}
}

.tag-row {
	margin-top: 14rem;
	display: flex;
	gap: 10rem;
	flex-wrap: wrap;

	.tag {
		padding: 6rem 18rem;
		background: #f3f3f3;
		border-radius: 4rem;
		color: #8f8f8f;
		font-size: 22rem;
		line-height: 1;
	}
}

.meta-row {
	margin-top: 15rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 16rem;

	.publish {
		color: #9a9a9a;
        font-size: 23rem;
		// font-size: 30rem;
	}

	.location {
		color: #6f6f6f;
        font-size: 22rem;
		// font-size: 30rem;
		display: inline-flex;
		align-items: center;
		gap: 6rem;
		text-align: right;
	}
}

</style>
